import { PerformanceDatapoint } from 'owa-analytics';
import {
    lazyGetAttachment,
    lazyRefreshThumbnailImageUrl,
    lazyShouldShowImageView,
} from 'owa-attachment-model-store';
import type { AttachmentWellViewState } from 'owa-attachment-well-data';
import { getUserMailboxInfo } from 'owa-client-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import {
    lazyGetLinksIdContainer,
    lazyGetSharingLinkInfo,
    lazyImageLinkPreviewCondition,
    LinksIdContainer,
    SharingLinkInfo,
} from 'owa-link-data';
import type { ImageGalleryItem } from 'owa-sxsdata';
import { trace } from 'owa-trace';

export default async function createImageGalleryForNewSxS(
    attachmentWellViewState: AttachmentWellViewState,
    linksContainerId?: string,
    useDocViewAttachments: boolean = false
): Promise<ImageGalleryItem[]> {
    const imageGallery: ImageGalleryItem[] = [];
    const getAttachment = await lazyGetAttachment.import();
    const refreshThumbnailImageUrl = await lazyRefreshThumbnailImageUrl.import();

    const shouldShowImageView = useDocViewAttachments
        ? await lazyShouldShowImageView.import()
        : null;

    const attachments = useDocViewAttachments
        ? attachmentWellViewState?.docViewAttachments
        : attachmentWellViewState?.imageViewAttachments;
    for (let i = 0; i < attachments?.length; i++) {
        const attachmentViewState = attachments[i];
        const attachmentModel = getAttachment(attachmentViewState.attachmentId);

        if (useDocViewAttachments && !shouldShowImageView(attachmentModel.model)) {
            continue;
        }

        try {
            await refreshThumbnailImageUrl(
                attachmentViewState.attachmentId,
                attachmentWellViewState.isReadOnly
            );
        } catch (error) {
            trace.warn(
                `Unable to refresh thumbnail image for ${attachmentViewState.attachmentId}. Got error ${error}`
            );
        }

        const imageGalleryItem: ImageGalleryItem = {
            downloadUrl: attachmentModel.download.url,
            id: attachmentViewState.attachmentId,
            previewUrl: attachmentModel.previewImage.url,
            thumbnailUrl: attachmentModel.thumbnailImage.url,
        };

        imageGallery.push(imageGalleryItem);
    }

    let numOfImageLinks: number = 0;
    const perfDatapoint = new PerformanceDatapoint('BeautifulImageLinksShownInPreview');

    const getLinksIdContainer = await lazyGetLinksIdContainer.import();
    const linksIdContainer: LinksIdContainer = getLinksIdContainer(linksContainerId);

    if (
        (isFeatureEnabled('doc-deprecateCloudy-preview') ||
            isFeatureEnabled('doc-deprecateCloudy-preview-thumbnailView')) &&
        linksIdContainer
    ) {
        const getSharingLinkInfo = await lazyGetSharingLinkInfo.import();
        const beautifulLinkIds: string[] = linksIdContainer.linkIds.concat(
            linksIdContainer.quotedBodyLinkIds
        );
        const imageLinkPreviewCondition = await lazyImageLinkPreviewCondition.import();
        for (let i = 0; i < beautifulLinkIds.length; i++) {
            const sharingLinkInformation: SharingLinkInfo = getSharingLinkInfo(beautifulLinkIds[i]);
            const isImageLinkPreviewable = imageLinkPreviewCondition(
                sharingLinkInformation.fileName,
                sharingLinkInformation.providerType
            );

            if (isImageLinkPreviewable) {
                numOfImageLinks++;

                const imageGalleryItem: ImageGalleryItem = {
                    downloadUrl: sharingLinkInformation.url,
                    id: {
                        Id: beautifulLinkIds[i],
                        RootItemId: beautifulLinkIds[i],
                        mailboxInfo: getUserMailboxInfo(),
                    },
                    previewUrl: sharingLinkInformation.previewUrl,
                    thumbnailUrl: sharingLinkInformation.thumbnailUrl,
                };

                if (imageGalleryItem.previewUrl) {
                    imageGallery.push(imageGalleryItem);
                }
            }
        }
    }
    perfDatapoint.addCustomData({
        countOfImageLinks: numOfImageLinks,
    });
    perfDatapoint.end();

    return imageGallery;
}
